import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch,
} from "firebase/firestore";
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Spinner } from "react-bootstrap";

import { db } from "../../../../shared/libs/firebaseConfig";
import { useAuth } from "../../../../shared/auth/AuthProvider";
import { AFFILIATE_COLLECTIONS } from "../../../../shared/utils/affiliateSchema";

const PAYOUT_STATUS_OPTIONS = [
  { value: "all", label: "ทุกสถานะ" },
  { value: "unpaid", label: "ยังไม่จ่าย" },
  { value: "paid", label: "จ่ายแล้ว" },
];

function getCurrentMonthKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatBaht(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("th-TH", {
    style: "currency",
    currency: "THB",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(n) ? n : 0);
}

function toDate(value) {
  try {
    const d =
      value?.toDate?.() ||
      (value instanceof Date ? value : value ? new Date(value) : null);
    return d && !Number.isNaN(d.getTime()) ? d : null;
  } catch {
    return null;
  }
}

function formatThaiDate(value) {
  const d = toDate(value);
  if (!d) return "-";
  return d.toLocaleDateString("th-TH", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getMonthKey(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function getAffiliateDisplayName(affiliate) {
  return (
    affiliate?.profile?.displayName ||
    affiliate?.contact?.email ||
    affiliate?.code ||
    affiliate?.userId ||
    "-"
  );
}

function maskBankAccount(value = "") {
  const raw = String(value || "").replace(/\D/g, "");
  if (!raw) return "-";
  if (raw.length <= 6) return raw;
  return `${raw.slice(0, 3)}${"*".repeat(Math.max(0, raw.length - 7))}${raw.slice(-4)}`;
}

function DetailItem({ label, children, align = "start" }) {
  return (
    <div className={align === "end" ? "text-md-end" : ""}>
      <div className="text-muted small">{label}</div>
      <div className="fw-semibold text-break">{children || "-"}</div>
    </div>
  );
}

export default function AdminAffiliateCommissions() {
  const { user } = useAuth();
  const adminUid = user?.uid || "";

  const [items, setItems] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState("");
  const [confirmGroup, setConfirmGroup] = useState(null);
  const [qrPreview, setQrPreview] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [monthKey, setMonthKey] = useState(getCurrentMonthKey);
  const [payoutStatus, setPayoutStatus] = useState("unpaid");
  const [affiliateId, setAffiliateId] = useState("all");
  const [courseId, setCourseId] = useState("all");

  const loadData = async () => {
    try {
      setLoading(true);
      setError("");
      const [commissionSnap, affiliateSnap] = await Promise.all([
        getDocs(collection(db, AFFILIATE_COLLECTIONS.commissions)),
        getDocs(collection(db, AFFILIATE_COLLECTIONS.affiliates)),
      ]);

      const rows = commissionSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = toDate(a?.createdAt)?.getTime() || 0;
        const tb = toDate(b?.createdAt)?.getTime() || 0;
        return tb - ta;
      });

      const affiliateRows = affiliateSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      affiliateRows.sort((a, b) =>
        getAffiliateDisplayName(a).localeCompare(getAffiliateDisplayName(b))
      );

      setItems(rows);
      setAffiliates(affiliateRows);
    } catch (e) {
      console.error(e);
      setError(e?.message || "โหลดค่าคอมมิชชันไม่สำเร็จ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const affiliateMap = useMemo(() => {
    const map = new Map();
    affiliates.forEach((item) => {
      if (item?.id) map.set(item.id, item);
      if (item?.userId) map.set(item.userId, item);
    });
    return map;
  }, [affiliates]);

  const courseOptions = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const id = item?.courseId || "";
      if (!id || map.has(id)) return;
      map.set(id, item?.courseTitle || id);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      if (monthKey && getMonthKey(item?.createdAt) !== monthKey) return false;
      if (payoutStatus !== "all" && (item?.payoutStatus || "unpaid") !== payoutStatus) return false;
      if (affiliateId !== "all" && item?.affiliateUserId !== affiliateId) return false;
      if (courseId !== "all" && item?.courseId !== courseId) return false;
      return true;
    });
  }, [items, monthKey, payoutStatus, affiliateId, courseId]);

  const summary = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => ({
        count: acc.count + 1,
        saleAmount: acc.saleAmount + Number(item?.saleAmount || 0),
        commissionAmount: acc.commissionAmount + Number(item?.commissionAmount || 0),
      }),
      { count: 0, saleAmount: 0, commissionAmount: 0 }
    );
  }, [filteredItems]);

  const groupedAffiliates = useMemo(() => {
    const map = new Map();

    filteredItems.forEach((item) => {
      const key = item?.affiliateUserId || item?.affiliateId || "unknown";
      if (!map.has(key)) {
        map.set(key, {
          key,
          affiliate: affiliateMap.get(key) || affiliateMap.get(item?.affiliateId) || null,
          items: [],
          totalSaleAmount: 0,
          totalCommissionAmount: 0,
          unpaidCount: 0,
          paidCount: 0,
          unpaidCommissionAmount: 0,
        });
      }

      const group = map.get(key);
      const isPaid = item?.payoutStatus === "paid" || item?.status === "paid";
      group.items.push(item);
      group.totalSaleAmount += Number(item?.saleAmount || 0);
      group.totalCommissionAmount += Number(item?.commissionAmount || 0);

      if (isPaid) {
        group.paidCount += 1;
      } else {
        group.unpaidCount += 1;
        group.unpaidCommissionAmount += Number(item?.commissionAmount || 0);
      }
    });

    return Array.from(map.values()).sort((a, b) => {
      if (b.unpaidCommissionAmount !== a.unpaidCommissionAmount) {
        return b.unpaidCommissionAmount - a.unpaidCommissionAmount;
      }
      return getAffiliateDisplayName(a.affiliate).localeCompare(getAffiliateDisplayName(b.affiliate));
    });
  }, [filteredItems, affiliateMap]);

  const selectedPeriodLabel = useMemo(() => {
    return monthKey || "ทั้งหมด";
  }, [monthKey]);

  const toggleDetails = (key) => {
    setExpandedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const markPaid = async () => {
    if (!confirmGroup?.key) return;

    const unpaidItems = confirmGroup.items.filter(
      (item) => item?.payoutStatus !== "paid" && item?.status !== "paid"
    );
    if (unpaidItems.length === 0) return;

    try {
      setUpdatingId(confirmGroup.key);
      setError("");
      setSuccess("");

      for (let i = 0; i < unpaidItems.length; i += 450) {
        const chunk = unpaidItems.slice(i, i + 450);
        const batch = writeBatch(db);

        chunk.forEach((item) => {
          batch.update(doc(db, AFFILIATE_COLLECTIONS.commissions, item.id), {
            payoutStatus: "paid",
            status: "paid",
            paidAt: serverTimestamp(),
            paidBy: adminUid,
            updatedAt: serverTimestamp(),
          });
        });

        await batch.commit();
      }

      const paidIds = new Set(unpaidItems.map((item) => item.id));

      setItems((prev) =>
        prev.map((item) =>
          paidIds.has(item.id)
            ? {
                ...item,
                payoutStatus: "paid",
                status: "paid",
                paidBy: adminUid,
                paidAt: new Date(),
                updatedAt: new Date(),
              }
            : item
        )
      );
      setSuccess(`บันทึกการชำระค่าคอมมิชชัน ${unpaidItems.length} รายการแล้ว`);
      setConfirmGroup(null);
    } catch (e) {
      console.error(e);
      setError(e?.message || "อัปเดตสถานะชำระเงินไม่สำเร็จ");
    } finally {
      setUpdatingId("");
    }
  };

  if (loading) {
    return (
      <div className="py-4 text-center text-muted">
        <Spinner animation="border" size="sm" /> กำลังโหลดค่าคอมมิชชัน...
      </div>
    );
  }

  return (
    <div>
      <h4 className="mb-1">Affiliate Commissions</h4>
      <div className="text-muted small mb-3">จัดการค่าคอมมิชชันและสถานะการจ่ายเงิน</div>

      {error ? <Alert variant="danger">{error}</Alert> : null}
      {success ? <Alert variant="success">{success}</Alert> : null}

      <Card className="border mb-3">
        <Card.Body>
          <Row className="g-3">
            <Col md={6} xl={3}>
              <Form.Label>เดือน</Form.Label>
              <Form.Control
                type="month"
                value={monthKey}
                onChange={(e) => setMonthKey(e.target.value)}
              />
              <Button
                type="button"
                variant="link"
                size="sm"
                className="px-0 mt-1 text-decoration-none"
                onClick={() => setMonthKey("")}
              >
                แสดงทั้งหมด
              </Button>
            </Col>
            <Col md={6} xl={3}>
              <Form.Label>สถานะจ่ายเงิน</Form.Label>
              <Form.Select value={payoutStatus} onChange={(e) => setPayoutStatus(e.target.value)}>
                {PAYOUT_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6} xl={3}>
              <Form.Label>Affiliate</Form.Label>
              <Form.Select value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)}>
                <option value="all">ทุก Affiliate</option>
                {affiliates.map((item) => (
                  <option key={item.id} value={item.userId || item.id}>
                    {getAffiliateDisplayName(item)}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6} xl={3}>
              <Form.Label>คอร์ส</Form.Label>
              <Form.Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                <option value="all">ทุกคอร์ส</option>
                {courseOptions.map(([id, title]) => (
                  <option key={id} value={id}>
                    {title}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-3 mb-3">
        <Col md={4}>
          <Card className="border h-100">
            <Card.Body>
              <div className="text-muted small">จำนวนรายการ</div>
              <div className="h5 mb-0">{summary.count.toLocaleString("th-TH")}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border h-100">
            <Card.Body>
              <div className="text-muted small">ยอดขายรวม</div>
              <div className="h5 mb-0">{formatBaht(summary.saleAmount)}</div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="border h-100">
            <Card.Body>
              <div className="text-muted small">ค่าคอมมิชชันรวม</div>
              <div className="h5 mb-0">{formatBaht(summary.commissionAmount)}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {groupedAffiliates.length === 0 ? (
        <Card className="border">
          <Card.Body className="text-center text-muted py-5">ยังไม่มีรายการค่าคอมมิชชัน</Card.Body>
        </Card>
      ) : (
        <div className="d-flex flex-column gap-3">
          {groupedAffiliates.map((group) => {
              const affiliate = group.affiliate || {};
              const bankAccount = affiliate?.bankAccount || {};
              const contact = affiliate?.contact || {};
              const isExpanded = !!expandedGroups[group.key];

              return (
                <Card key={group.key} className="border">
                  <Card.Body>
                    <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap mb-3">
                      <div className="min-w-0">
                        <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                          <div className="fw-semibold text-break">
                            {getAffiliateDisplayName(affiliate) || group.key}
                          </div>
                          <Badge bg={group.unpaidCount > 0 ? "warning" : "success"} text={group.unpaidCount > 0 ? "dark" : undefined}>
                            unpaid {group.unpaidCount}
                          </Badge>
                          <Badge bg="secondary">paid {group.paidCount}</Badge>
                        </div>

                        <div className="text-muted small text-break">
                          Code: <code>{affiliate?.code || group.items[0]?.affiliateCode || "-"}</code>
                          {" · "}
                          รอบที่เลือก: {selectedPeriodLabel}
                        </div>
                      </div>

                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setQrPreview({ affiliate, group })}
                          className="flex-shrink-0"
                        >
                          QR รับเงิน
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline-primary"
                          onClick={() => toggleDetails(group.key)}
                          className="flex-shrink-0"
                        >
                          {isExpanded ? "ซ่อนรายละเอียด" : "รายละเอียด"}
                        </Button>
                        {group.unpaidCount > 0 ? (
                          <Button
                            type="button"
                            size="sm"
                            variant="success"
                            onClick={() => setConfirmGroup(group)}
                            disabled={updatingId === group.key}
                            className="flex-shrink-0"
                          >
                            ชำระแล้ว
                          </Button>
                        ) : (
                          <div className="text-success small fw-semibold flex-shrink-0">จ่ายครบแล้ว</div>
                        )}
                      </div>
                    </div>

                    <Row className="g-3 align-items-start">
                      <Col md={3}>
                        <DetailItem label="ติดต่อ">
                          <span>{contact?.email || "-"}</span>
                          {contact?.phoneNumber ? (
                            <div className="text-muted small fw-normal">{contact.phoneNumber}</div>
                          ) : null}
                        </DetailItem>
                      </Col>
                      <Col md={3}>
                        <DetailItem label="บัญชีรับเงิน">
                          <span>{bankAccount?.bankName || "-"}</span>
                          <div className="text-muted small fw-normal text-break">
                            {bankAccount?.accountName || "-"} · {maskBankAccount(bankAccount?.accountNumber)}
                          </div>
                        </DetailItem>
                      </Col>
                      <Col md={3}>
                        <DetailItem label="จำนวนยอดขาย">{group.items.length.toLocaleString("th-TH")} รายการ</DetailItem>
                      </Col>
                      <Col md={3}>
                        <DetailItem label="สถานะจ่ายเงิน">
                          ยังไม่จ่าย {group.unpaidCount.toLocaleString("th-TH")} / จ่ายแล้ว {group.paidCount.toLocaleString("th-TH")}
                        </DetailItem>
                      </Col>
                    </Row>

                    <hr className="my-3" />

                    <Row className="g-3">
                      <Col xs={6} md={3}>
                        <DetailItem label="ยอดขาย" align="end">
                          {formatBaht(group.totalSaleAmount)}
                        </DetailItem>
                      </Col>
                      <Col xs={6} md={3}>
                        <DetailItem label="ค่าคอมรวม" align="end">
                          {formatBaht(group.totalCommissionAmount)}
                        </DetailItem>
                      </Col>
                      <Col xs={6} md={3}>
                        <DetailItem label="ค้างจ่าย" align="end">
                          {formatBaht(group.unpaidCommissionAmount)}
                        </DetailItem>
                      </Col>
                      <Col xs={6} md={3}>
                        <DetailItem label="Affiliate ID" align="end">{group.key}</DetailItem>
                      </Col>
                    </Row>

                    {isExpanded ? (
                      <div className="mt-4">
                        <div className="fw-semibold mb-2">รายละเอียดรายการขาย</div>
                        <div className="d-flex flex-column gap-2">
                          {group.items.map((item) => {
                            const isPaid = item.payoutStatus === "paid" || item.status === "paid";

                            return (
                              <div key={item.id} className="border rounded p-3">
                                <div className="d-flex align-items-start justify-content-between gap-3 flex-wrap">
                                  <div className="min-w-0">
                                    <div className="fw-semibold text-break">
                                      {formatThaiDate(item.createdAt)} · {item.courseTitle || item.courseId || "-"}
                                    </div>
                                    <div className="text-muted small text-break">
                                      {item.coursePackageLabel || item.coursePackage || "-"}
                                    </div>
                                    <div className="text-muted small text-break">
                                      ผู้ซื้อ: {item?.buyerSnapshot?.fullName || item.buyerUserId || "-"}
                                      {item?.buyerSnapshot?.email ? ` · ${item.buyerSnapshot.email}` : ""}
                                    </div>
                                    {item.verifiedTransactionRef ? (
                                      <div className="text-muted small text-break">
                                        Ref: <code>{item.verifiedTransactionRef}</code>
                                      </div>
                                    ) : null}
                                  </div>
                                  <Badge bg={isPaid ? "success" : "warning"} text={isPaid ? undefined : "dark"}>
                                    {item.payoutStatus || "unpaid"}
                                  </Badge>
                                </div>

                                <Row className="g-3 mt-2">
                                  <Col xs={6} md={3}>
                                    <DetailItem label="ยอดขาย">{formatBaht(item.saleAmount)}</DetailItem>
                                  </Col>
                                  <Col xs={6} md={3}>
                                    <DetailItem label="ค่าคอม %">
                                      {Number(item.commissionPercent || 0).toLocaleString("th-TH")}%
                                    </DetailItem>
                                  </Col>
                                  <Col xs={6} md={3}>
                                    <DetailItem label="ค่าคอม">{formatBaht(item.commissionAmount)}</DetailItem>
                                  </Col>
                                  <Col xs={6} md={3}>
                                    <DetailItem label="สถานะ">{item.status || "-"} / {item.payoutStatus || "unpaid"}</DetailItem>
                                  </Col>
                                </Row>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                  </Card.Body>
                </Card>
              );
          })}
        </div>
      )}

      <Modal show={!!qrPreview} onHide={() => setQrPreview(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>QR รับเงิน</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {qrPreview?.affiliate?.qrCode?.imageUrl ? (
            <>
              <img
                src={qrPreview.affiliate.qrCode.imageUrl}
                alt="Affiliate payout QR"
                style={{ maxWidth: "100%", width: 320, borderRadius: 8 }}
              />
              <div className="text-muted small mt-3 text-break">
                {qrPreview.affiliate.qrCode.storagePath || "-"}
              </div>
            </>
          ) : (
            <div className="text-muted py-4">ยังไม่มี QR รับเงิน</div>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!confirmGroup} onHide={() => setConfirmGroup(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>ยืนยันการชำระค่าคอมมิชชัน</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ต้องการเปลี่ยนสถานะค่าคอมมิชชันของ Affiliate นี้เป็นชำระแล้วหรือไม่
          <div className="mt-2">
            <div className="fw-semibold">
              {getAffiliateDisplayName(confirmGroup?.affiliate) || confirmGroup?.key || "-"}
            </div>
            <div className="text-muted small">
              {confirmGroup?.unpaidCount || 0} รายการ · รวม {formatBaht(confirmGroup?.unpaidCommissionAmount)}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setConfirmGroup(null)} disabled={!!updatingId}>
            ยกเลิก
          </Button>
          <Button variant="success" onClick={markPaid} disabled={!!updatingId}>
            {updatingId ? "กำลังบันทึก..." : "ชำระแล้ว"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
